import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import {
  legacySiteCableHistoryId,
  type LegacySiteCableHistoryId,
} from './LegacySiteCableHistory.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.legacy_site_merge_cable_history_ */
export type LegacySiteMergeCableHistoryId = string & {
  __brand: 'LegacySiteMergeCableHistoryId'
}

/** Represents the table public.legacy_site_merge_cable_history_ */
export default interface LegacySiteMergeCableHistoryTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    LegacySiteMergeCableHistoryId,
    LegacySiteMergeCableHistoryId | undefined,
    LegacySiteMergeCableHistoryId
  >

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.uuid */
  fromCableHistoryId: ColumnType<
    LegacySiteCableHistoryId,
    LegacySiteCableHistoryId,
    LegacySiteCableHistoryId
  >

  /** Database type: pg_catalog.uuid */
  toCableHistoryId: ColumnType<
    LegacySiteCableHistoryId,
    LegacySiteCableHistoryId,
    LegacySiteCableHistoryId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>
}

export type LegacySiteMergeCableHistory =
  Selectable<LegacySiteMergeCableHistoryTable>

export type LegacySiteMergeCableHistoryInitializer =
  Insertable<LegacySiteMergeCableHistoryTable>

export type LegacySiteMergeCableHistoryMutator =
  Updateable<LegacySiteMergeCableHistoryTable>

export const legacySiteMergeCableHistoryId =
  z.string() as unknown as z.Schema<LegacySiteMergeCableHistoryId>

export const legacySiteMergeCableHistory = z.object({
  id: legacySiteMergeCableHistoryId,
  siteId: legacySiteId,
  fromCableHistoryId: legacySiteCableHistoryId,
  toCableHistoryId: legacySiteCableHistoryId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
}) as unknown as z.Schema<LegacySiteMergeCableHistory>

export const legacySiteMergeCableHistoryInitializer = z.object({
  id: legacySiteMergeCableHistoryId.optional(),
  siteId: legacySiteId,
  fromCableHistoryId: legacySiteCableHistoryId,
  toCableHistoryId: legacySiteCableHistoryId,
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteMergeCableHistoryInitializer>

export const legacySiteMergeCableHistoryMutator = z.object({
  id: legacySiteMergeCableHistoryId.optional(),
  siteId: legacySiteId.optional(),
  fromCableHistoryId: legacySiteCableHistoryId.optional(),
  toCableHistoryId: legacySiteCableHistoryId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
}) as unknown as z.Schema<LegacySiteMergeCableHistoryMutator>
